import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
  EmptyState,
  PageHeader,
  StatusBadge,
} from "../../components/dashboard/DashboardUI";
import { getSubscriptionsOverview } from "../../redux/actions/SubscriptionActions";
import { SERVICE_NAMES } from "../../utils/requestDisplay";

const PAGE_SIZE = 25;
// A subscription ending within this number of days is flagged
const SOON_DAYS = 7;

const BILLINGS = { monthly: "Mensuel", yearly: "Annuel" };

const digits = (value) => String(value || "").replace(/\D/g, "");
const serviceName = (code) => SERVICE_NAMES[code] || code;
const ownerName = (company) =>
  [company.user?.firstname, company.user?.lastname].filter(Boolean).join(" ");
const daysLeft = (subscription) =>
  moment(subscription.endsOn).diff(moment(), "days");

// Only the most recent subscription of each service is kept
const latestByService = (subscriptions) => {
  const latest = {};

  subscriptions.forEach((subscription) => {
    const current = latest[subscription.service];
    if (!current || new Date(subscription.endsOn) > new Date(current.endsOn)) {
      latest[subscription.service] = subscription;
    }
  });

  return Object.values(latest).sort(
    (a, b) => new Date(a.endsOn) - new Date(b.endsOn),
  );
};

// Badge of a company: expired, ending soon or active
const stateOf = (subscriptions) => {
  const active = subscriptions.filter((subscription) => subscription.isValid);

  if (active.length === 0) return { status: "failed", label: "Expiré" };

  const soonest = Math.min(...active.map(daysLeft));
  if (soonest <= SOON_DAYS) {
    return {
      status: "in_progress",
      label:
        soonest <= 0
          ? "Expire aujourd'hui"
          : `Expire dans ${soonest} jour${soonest > 1 ? "s" : ""}`,
    };
  }

  return { status: "fulfilled", label: "Actif" };
};

// Companies with a monthly or yearly subscription, to know which clients
// are entitled to a service
function OperatorSubscriptions() {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.authUser);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [service, setService] = useState("");
  const [billing, setBilling] = useState("");
  const [includeExpired, setIncludeExpired] = useState(false);
  const [visible, setVisible] = useState(PAGE_SIZE);

  useEffect(() => {
    if (!user?.token) return;

    setLoading(true);
    dispatch(
      getSubscriptionsOverview(user.token, includeExpired, (response) => {
        setRows(
          response.data.map((row) => ({
            company: row.company,
            subscriptions: latestByService(row.subscriptions || []),
          })),
        );
        setError(response.success ? null : response.message);
        setLoading(false);
      }),
    );
  }, [user?.token, includeExpired, dispatch]);

  const services = [
    ...new Set(rows.flatMap((row) => row.subscriptions.map((s) => s.service))),
  ];

  const term = search.trim().toLowerCase();
  const termDigits = digits(term);
  const filtered = rows
    .map((row) => ({
      ...row,
      // Only the subscriptions matching the service and billing filters
      subscriptions: row.subscriptions.filter(
        (subscription) =>
          (!service || subscription.service === service) &&
          (!billing || subscription.billing === billing),
      ),
    }))
    .filter(({ company, subscriptions }) => {
      if (subscriptions.length === 0) return false;
      if (!term) return true;

      const texts = [company.businessName, ownerName(company), company.email];
      const phones = [
        company.mobile,
        company.whatsApp,
        company.user?.mobile,
        company.user?.whatsApp,
      ];

      return (
        texts.some((text) => text?.toLowerCase().includes(term)) ||
        (termDigits.length >= 4 &&
          phones.some((phone) => digits(phone).includes(termDigits)))
      );
    });

  const reset = (setter) => (event) => {
    setter(event.target.value);
    setVisible(PAGE_SIZE);
  };

  return (
    <>
      <PageHeader
        title="Abonnements"
        lead="Les entreprises abonnées à un service, en formule mensuelle ou annuelle."
      />

      <div className="dash-card">
        <div className="dash-filters">
          <input
            type="search"
            placeholder="Rechercher une entreprise, un propriétaire ou un numéro"
            value={search}
            onChange={reset(setSearch)}
            aria-label="Rechercher"
          />
          <select
            value={service}
            onChange={reset(setService)}
            aria-label="Service"
          >
            <option value="">Tous les services</option>
            {services.map((code) => (
              <option key={code} value={code}>
                {serviceName(code)}
              </option>
            ))}
          </select>
          <select
            value={billing}
            onChange={reset(setBilling)}
            aria-label="Formule"
          >
            <option value="">Toutes les formules</option>
            {Object.entries(BILLINGS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <select
            value={includeExpired ? "all" : "active"}
            onChange={(event) => {
              setIncludeExpired(event.target.value === "all");
              setVisible(PAGE_SIZE);
            }}
            aria-label="État"
          >
            <option value="active">Abonnements en cours</option>
            <option value="all">En cours et expirés</option>
          </select>
        </div>

        {loading ? (
          <p className="dash-loading">Chargement des abonnements…</p>
        ) : error ? (
          <EmptyState title="Liste indisponible">{error}</EmptyState>
        ) : filtered.length === 0 ? (
          <EmptyState title="Aucun abonnement">
            {rows.length
              ? "Aucune entreprise ne correspond à ces critères."
              : "Aucune entreprise n'a d'abonnement en cours."}
          </EmptyState>
        ) : (
          <>
            <div className="dash-table-wrap">
              <table className="dash-table is-responsive">
                <thead>
                  <tr>
                    <th>Entreprise</th>
                    <th>Contact</th>
                    <th>Abonnements</th>
                    <th>État</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered
                    .slice(0, visible)
                    .map(({ company, subscriptions }) => {
                      const state = stateOf(subscriptions);
                      const phone = company.user?.mobile || company.mobile;
                      const whatsApp =
                        company.user?.whatsApp || company.whatsApp;

                      return (
                        <tr key={company._id}>
                          <td>
                            <span className="dash-cell-title">
                              {company.businessName || "Sans nom"}
                            </span>
                            <span className="dash-cell-sub">
                              {company.businessType}
                            </span>
                          </td>
                          <td data-label="Contact">
                            {ownerName(company) || "—"}
                            <span className="dash-cell-sub">
                              {[phone, whatsApp && `WhatsApp ${whatsApp}`]
                                .filter(Boolean)
                                .join(" · ") || "Pas de numéro"}
                            </span>
                          </td>
                          <td data-label="Abonnements">
                            <ul className="dash-sub-list">
                              {subscriptions.map((subscription) => (
                                <li
                                  key={subscription._id}
                                  className={
                                    subscription.isValid ? "" : "is-expired"
                                  }
                                >
                                  <strong>
                                    {serviceName(subscription.service)}
                                  </strong>{" "}
                                  ·{" "}
                                  {BILLINGS[
                                    subscription.billing
                                  ]?.toLowerCase()}{" "}
                                  ·{" "}
                                  {subscription.isValid
                                    ? "jusqu'au"
                                    : "expiré le"}{" "}
                                  {moment(subscription.endsOn).format(
                                    "DD/MM/YYYY",
                                  )}
                                </li>
                              ))}
                            </ul>
                          </td>
                          <td data-label="État">
                            <StatusBadge
                              status={state.status}
                              label={state.label}
                            />
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
            {filtered.length > visible && (
              <div className="dash-more">
                <button
                  type="button"
                  className="dash-button is-secondary"
                  onClick={() => setVisible((count) => count + PAGE_SIZE)}
                >
                  Afficher plus
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

export default OperatorSubscriptions;
